import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, X } from 'lucide-react';
import { getCheckoutUrl } from '../utils/utm';

export const Hero = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [stickyDismissed, setStickyDismissed] = useState(false);
  const videoPlaceholderRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const checkoutUrl = getCheckoutUrl();

  // Load Wistia scripts
  useEffect(() => {
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.head.appendChild(playerScript);

    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/umm704q53i.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
  }, []);

  // Apply/remove sticky styles via direct DOM manipulation (preserves Wistia state)
  useEffect(() => {
    const player = videoPlayerRef.current;
    if (!player) return;

    if (isSticky && !stickyDismissed) {
      player.style.position = 'fixed';
      player.style.top = '80px';
      player.style.right = '16px';
      player.style.width = window.innerWidth < 768 ? '260px' : '380px';
      player.style.zIndex = '9999';
      player.style.borderRadius = '8px';
      player.style.boxShadow = '0 8px 40px rgba(0,0,0,0.5)';
      player.style.overflow = 'visible';
    } else {
      player.style.position = '';
      player.style.top = '';
      player.style.right = '';
      player.style.width = '';
      player.style.zIndex = '';
      player.style.borderRadius = '';
      player.style.boxShadow = '';
      player.style.overflow = '';
    }
  }, [isSticky, stickyDismissed]);

  // IntersectionObserver: sticky when video scrolls out of view
  useEffect(() => {
    const placeholder = videoPlaceholderRef.current;
    if (!placeholder) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !stickyDismissed) {
          setIsSticky(true);
        } else if (entry.isIntersecting) {
          setIsSticky(false);
          // Reset dismissed when user scrolls back to video
          setStickyDismissed(false);
        }
      },
      { threshold: 0 }
    );

    observer.observe(placeholder);
    return () => observer.disconnect();
  }, [stickyDismissed]);

  const handleCloseSticky = (e) => {
    e.stopPropagation();
    setStickyDismissed(true);
    setIsSticky(false);
  };

  const scrollToOffer = () => {
    const element = document.getElementById('offer-details');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const showSticky = isSticky && !stickyDismissed;

  return (
    <section 
      data-testid="hero-section"
      className="bg-white pt-12 pb-16 md:pt-20 md:pb-24 px-8 md:px-12"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1"
          >
            {/* Pre-headline */}
            <div className="eyebrow mb-6">
              <span className="font-editorial text-xs tracking-widest uppercase text-amber-600 font-bold">
                <span className="hidden md:inline">Charles Payne's Proven Investing Framework</span>
                <span className="md:hidden">Charles Payne's Proven Framework</span>
              </span>
            </div>

            {/* Main Headline - Bold */}
            <h1 className="font-editorial text-3xl md:text-[3.5rem] font-bold text-gray-900 leading-[1.1] mb-6">
              Stop Guessing. Start Investing With a System{' '}
              <span className="gradient-accent">That Actually Works.</span>
            </h1>

            {/* Subhead */}
            <p className="font-sans text-lg text-gray-600 leading-relaxed mb-8 max-w-lg">
              Charles Payne breaks down the same investing framework he applies on national television every day and teaches it to everyday investors who are tired of reacting, guessing, and hoping. Watch the short video above to see how it works.
            </p>

            {/* CTA Row */}
            <div className="flex flex-col items-start gap-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href={checkoutUrl}
                  data-testid="hero-cta-button"
                  className="btn-primary"
                >
                  Get Started Now for $7
                </a>

                {/* Here's What You Get Link */}
                <button
                  onClick={scrollToOffer}
                  data-testid="scroll-to-offer-btn"
                  className="group flex items-center gap-2 font-sans text-sm text-amber-600 hover:text-amber-700 font-medium transition-colors"
                >
                  <span>See What You Get</span>
                  <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </button>
              </div>

              <p className="font-sans text-sm text-gray-500 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                100% satisfaction guarantee
              </p>
            </div>
          </motion.div>

          {/* Right Column - Video */}
          <div className="order-1 lg:order-2">
            <div ref={videoPlaceholderRef}>
              <div 
                ref={videoPlayerRef}
                data-testid="vsl-player"
                className="relative bg-gray-900 shadow-2xl overflow-hidden"
              >
                {/* Close button - only when sticky */}
                {showSticky && (
                  <button
                    onClick={handleCloseSticky}
                    className="absolute -top-4 -right-4 z-[10000] w-9 h-9 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.3)] transition-colors cursor-pointer border border-gray-200"
                    data-testid="sticky-close-btn"
                    style={{ pointerEvents: 'all' }}
                  >
                    <X className="w-4.5 h-4.5" strokeWidth={2.5} />
                  </button>
                )}

                {/* Corner accent - only when inline */}
                {!showSticky && (
                  <div className="absolute top-0 left-0 w-16 h-16 border-l-4 border-t-4 border-amber-600 z-10 pointer-events-none" />
                )}
                
                {/* Wistia Embed */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    wistia-player[media-id='umm704q53i']:not(:defined) {
                      background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/umm704q53i/swatch');
                      display: block;
                      filter: blur(5px);
                      padding-top: 56.25%;
                    }
                  `
                }} />
                <wistia-player 
                  media-id="umm704q53i" 
                  seo="false" 
                  aspect="1.7777777777777777"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
